/** Service Line manifest: compact Form create + Detail panel, with SOR-item and task relation boards. */
import { ActivityEntityName, DomainType, EntityViewDisplay, PortalClientSecurityRight, ServiceLine, SurfaceType } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { PLANNED_TASK_ENTITY } from '@workbuddy/entities/planned.task/planned.task.model';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import { PlannedWorkCol } from '../shared/planned.work.board.columns';

export const serviceLineManifest = registerManifest<ServiceLine>({
  entity: ActivityEntityName.ServiceLine,
  slug: 'service-line',
  board: {
    supportedDisplays: [EntityViewDisplay.Table],
    defaults: {
      header: {
        title: 'Service Lines',
        showSearch: true,
        showPaginator: false,
      },
      table: {
        fillerColumn: true,
        columns: {
          byField: {
            name: PlannedWorkCol.title,
            dateStart: PlannedWorkCol.date,
            dateEnd: PlannedWorkCol.date,
            'jobDefaults.workCategory': PlannedWorkCol.tag,
            defaultResources: PlannedWorkCol.crew,
            defaultContractor: PlannedWorkCol.crew,
            status: PlannedWorkCol.status,
          },
        },
      },
    },
  },
  routes: {
    root: '/crm/service-plans',
  },
  surfaces: {
    [SurfaceType.Form]: () => import('./surface/create/service.line.create.surface').then(module => module.serviceLineCreateFormSurface),
    [SurfaceType.Detail]: () => import('./surface/detail/service.line.detail.surface').then(module => module.serviceLineDetailSurface),
  },
  operations: {
    create: { surface: SurfaceType.Form, presentation: { mode: 'dialog', title: 'New service line', width: 'min(640px, calc(100vw - 40px))' } },
    edit: { surface: SurfaceType.Detail },
    preview: { surface: SurfaceType.Detail },
  },
  relations: {
    [ActivityEntityName.PlannedItem]: () => import('./relations/service.line.items.relation').then(module => module.serviceLineItemsBoardBuilder),
    [PLANNED_TASK_ENTITY]: () => import('./relations/service.line.tasks.relation').then(module => module.serviceLineTasksBoardBuilder),
    [ActivityEntityName.Visit]: () => import('./relations/service.line.visits.relation').then(module => module.serviceLineVisitsBoardBuilder),
  },
  domains: {
    [DomainType.ClientPortal]: {
      routes: {
        root: '/services',
        // No full page in the portal: the client's Service Line workspace IS the inspector, and the
        // page route behind it has been removed. `null` hides Open full page and Open in new tab
        // rather than letting the generic derived route offer a link to nothing.
        record: () => null,
      },
      surfaces: {
        [SurfaceType.Manage]: () => import('./surface/manage/service.line.client.manage.surface').then(module => module.clientServiceLineManageSurface),
      },
      operations: {
        edit: { surface: SurfaceType.Manage, authorize: ({ injector }) => injector.get(RightsService).hasRights(PortalClientSecurityRight.CanAccessServices) },
        preview: { surface: SurfaceType.Manage, authorize: ({ injector }) => injector.get(RightsService).hasRights(PortalClientSecurityRight.CanAccessServices) },
      },
      relations: {
        [ActivityEntityName.PlannedTaskOutline]: () => import('./relations/service.line.client.tasks.relation').then(module => module.clientServiceLineTasksBoardBuilder),
        [ActivityEntityName.Visit]: () => import('./relations/service.line.client.visits.relation').then(module => module.clientServiceLineVisitsBoardBuilder),
        [ActivityEntityName.Job]: () => import('./relations/service.line.client.jobs.relation').then(module => module.clientServiceLineJobsBoardBuilder),
      },
    },
  },
});
